import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, MapPin, Globe, Tag, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import SectionHeader from "./SectionHeader";
import EmptyState from "./EmptyState";

// Filtres rapides (liens vers la page partenaires / bons plans).
const FILTERS = [
  { label: "Proximité", to: "/autour-de-moi" },
  { label: "Par catégorie", to: "/categories" },
  { label: "Avis", to: "/avis" },
  { label: "Nouveautés", to: "/partenaires" },
];

export default function FeaturedPros() {
  const [pros, setPros] = useState(null);

  useEffect(() => {
    api.get("/featured-partners?limit=6").then((r) => setPros(r.data?.partners || [])).catch(() => setPros([]));
  }, []);

  if (pros === null) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="pros-section">
      <SectionHeader
        eyebrow="Professionnels"
        title="Les pros à découvrir"
        subtitle="Commerçants et artisans locaux à soutenir près de chez vous."
        linkTo="/partenaires"
        linkLabel="Tous les partenaires"
        linkTestId="view-all-pros-btn"
      />

      <div className="mt-5 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Link
            key={f.label}
            to={f.to}
            data-testid="pro-filter-pill"
            className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-medium text-[#27272A] hover:border-[#E05638]/50 hover:text-[#E05638] transition-colors"
          >
            {f.label}
          </Link>
        ))}
      </div>

      {pros.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={Store}
            title="Nos premiers partenaires arrivent"
            text="Vous êtes commerçant ou artisan en Eure-et-Loir ? Rejoignez la plateforme gratuitement (0 € pendant 12 mois)."
            ctaLabel="Devenir partenaire"
            ctaTo="/devenir-partenaire"
            testId="pros-empty"
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {pros.map((p) => {
            const siteUrl = p.website ? (p.website.startsWith("http") ? p.website : `https://${p.website}`) : null;
            return (
              <div
                key={p.id}
                data-testid="pro-card"
                className="rounded-2xl border border-slate-200 bg-white p-4 hover:-translate-y-0.5 hover:shadow-md hover:border-[#E05638]/30 transition-all duration-200 flex flex-col"
              >
                <div className="flex items-center gap-3">
                  <Link to={`/commerce/${p.id}`} className="shrink-0" aria-label={p.name}>
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 grid place-items-center">
                      {p.logo || p.cover_image
                        ? <img src={p.logo || p.cover_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                        : <Store size={22} className="text-slate-300" />}
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/commerce/${p.id}`} className="font-heading font-bold text-[#172A3A] line-clamp-1 hover:text-[#E05638] transition-colors">
                      {p.name}
                    </Link>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                      {p.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.city}</span>}
                      {p.category && <span className="truncate">· {p.category}</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1.5">
                  <Link to={`/commerce/${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#FFF4EC] text-[#E05638] border border-orange-200 px-2.5 py-1 text-xs font-semibold hover:bg-orange-100 transition-colors">
                    <Store size={12} /> Mini-site
                  </Link>
                  {p.offer_count > 0 && (
                    <Link to={p.offer_id ? `/offre/${p.offer_id}` : `/commerce/${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-xs font-semibold hover:bg-emerald-100 transition-colors">
                      <Tag size={12} /> {p.offer_count > 1 ? `${p.offer_count} offres` : "Son offre"}
                    </Link>
                  )}
                  {siteUrl && (
                    <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-1 text-xs font-semibold hover:bg-slate-100 transition-colors">
                      <Globe size={12} /> Site <ArrowUpRight size={11} className="opacity-60" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
